// Closing tab Step 1.
//
// Top half: live pre-flight panel (4 lane passes, combined DQR, escrow
// funded). Bottom half: 4 Closing
// certifications rendered with the unified <CertificationItemRow> — the
// same three-button CERTIFY / DECLINE / ESCALATE structure + OPEN FORM
// modal that the analyst Section 2 Additional Certifications use.

import { Check, Minus, X } from "lucide-react";
import { useMemo } from "react";
import { Card, Badge } from "@/design-system/components/ui";
import {
  useDealAssignments,
  useDealEscrowStatus,
  useDealOutcome,
  useEscrowSetup,
  useLaneAssessments,
  useUnderwritingCertifications,
} from "@/hooks/dealFlow/useDealFlow";
import { formatCurrency } from "@/design-system/lib/format";
import { CertificationItemRow } from "@/components/DealFlow/AdditionalCertifications";
import type { CertConfig } from "@/components/DealFlow/AdditionalCertifications";
import type {
  DealEscrowStatusDto,
  UnderwritingCertificationDto,
} from "@/api/dealFlow/types";
import { useDateFormatter } from "@/utils/datetime";

interface ReviewAndCertifyStepProps {
  dealId: string;
  /** True when the step is collapsed/locked due to outer state machine. */
  readOnly?: boolean;
}

const CERTS: ReadonlyArray<CertConfig> = [
  {
    certificationCode: "Close.S1.UnderwritingSignedOff",
    title: "All underwriting + analyst work signed off",
    description:
      "I confirm Underwriting Section 6 verification and lane Pass/Fail for A1-A4 are complete and consistent with the deal package.",
  },
  {
    certificationCode: "Close.S1.FinalAddendumSigned",
    title: "Approved loan terms on file",
    description:
      "I confirm the approved loan terms (loan amount, rate, and term) are on file and consistent with the approved deal.",
  },
  {
    certificationCode: "Close.S1.EscrowFundedByBuyer",
    title: "Escrow funded by Buyer",
    description:
      "I confirm escrow has been funded by the buyer and the funding shows as verified in Underwriting Section 2: by A1 on a Legacy escrow, or by Escrow.com on an Escrow.com escrow.",
  },
  {
    certificationCode: "Close.S1.LoanDocumentsSigned",
    title: "All loan documents signed and on file",
    description:
      "I confirm the borrower has executed every loan document required by the addendum and all copies are filed in the deal's Documents tab.",
  },
];

const CHECK_LABEL: Record<CheckStatus, string> = {
  ok: "Pass",
  fail: "Open",
  na: "N/A",
};

type CheckStatus = "ok" | "fail" | "na";

export function ReviewAndCertifyStep({ dealId, readOnly = false }: ReviewAndCertifyStepProps) {
  const assignments = useDealAssignments(dealId);
  const laneAssessments = useLaneAssessments(dealId);
  const outcome = useDealOutcome(dealId);
  const escrow = useEscrowSetup(dealId);
  // The escrow item reads the shared escrow predicate, so it states the facts
  // on either model (owner ruling, 2026-10-09). Like every pre-flight row it
  // gates nothing: the Closing Coordinator certifies once satisfied.
  const escrowStatus = useDealEscrowStatus(dealId);
  // Closing Step 1 certs live on the UnderwritingCertification entity at
  // Section 1 (the Close.S1.* prefix). They use the same unified state
  // machine as the analyst Section 2 Additional Certifications.
  const certs = useUnderwritingCertifications(dealId, "Closing", 1);
  const { date } = useDateFormatter();

  const rowByCode = useMemo(() => {
    const map = new Map<string, UnderwritingCertificationDto>();
    for (const c of certs.data ?? []) map.set(c.certificationCode, c);
    return map;
  }, [certs.data]);

  const laneChecks = (["A1", "A2", "A3", "A4"] as const).map((lane) => {
    const assignment = (assignments.data ?? []).find((a) => a.roleCode === lane);
    const assessment = (laneAssessments.data ?? []).find((la) => la.lane === lane);
    if (assignment?.status === "NotApplicable") return { lane, status: "na" as const };
    if (assessment?.outcomePassFail === "Pass") return { lane, status: "ok" as const };
    return { lane, status: "fail" as const };
  });

  const combinedDqr = outcome.data?.combinedDealQualityRating ?? null;
  const overallPass = outcome.data?.overallPass ?? false;
  const escrowFunded = !!escrowStatus.data?.fundingVerified;
  const escrowDetail = describeEscrow(
    escrowStatus.data,
    escrow.data?.fundingVerifiedAmount ?? escrow.data?.amountUsd ?? null,
    date,
  );

  // A cert is "resolved" once it reaches any terminal status — mirrors the
  // analyst Section 2 rollup. Pre-flight checks are surfaced as read-only
  // context above; they no longer gate the row, because declining or
  // escalating a cert when a pre-flight check is still open is exactly the
  // judgment the Closing Coordinator needs to record.
  const isResolved = (code: string): boolean => {
    const s = rowByCode.get(code)?.status;
    return s === "Certified" || s === "Declined" || s === "Escalated";
  };
  const totalSigned = CERTS.filter((c) => isResolved(c.certificationCode)).length;
  const allSigned = totalSigned === CERTS.length;

  return (
    <div className="flex flex-col gap-space-3" data-testid="closing-step-1">
      <Card>
        <header className="mb-space-3 flex items-start justify-between gap-space-3">
          <div>
            <h3 className="font-display text-h4 text-onyx m-0">
              Step 1 · Review and Certify Everything in Order
            </h3>
            <p className="text-caption text-gray-mid m-0">
              Live read-only summary of the prerequisites for closing. It
              gates nothing: certify each item below once you have confirmed it.
            </p>
          </div>
          {allSigned ? (
            <Badge status="gold">
              All {CERTS.length} certifications resolved
            </Badge>
          ) : (
            <Badge status="archived">
              {totalSigned} / {CERTS.length} resolved
            </Badge>
          )}
        </header>

        <ul
          className="flex flex-col gap-space-2 m-0 p-0 list-none"
          data-testid="closing-step-1-preflight"
        >
          {laneChecks.map((c) => {
            const ass = (laneAssessments.data ?? []).find((la) => la.lane === c.lane);
            const detail =
              c.status === "ok" && ass
                ? `DQR=${ass.dealQualityRating} on ${date(ass.assessedAt)}`
                : c.status === "na"
                  ? "Marked Not Applicable"
                  : "Awaiting Pass assessment";
            return (
              <CheckRow
                key={c.lane}
                status={c.status}
                label={`${c.lane} issued Lane Pass`}
                detail={detail}
              />
            );
          })}
          <CheckRow
            status={overallPass ? "ok" : "fail"}
            label="Combined DQR"
            detail={
              combinedDqr === null
                ? "DealOutcome not finalized"
                : `Combined DQR = ${combinedDqr}${overallPass ? " (overall Pass)" : " (overall Fail)"}`
            }
          />
          <CheckRow
            status={escrowFunded ? "ok" : "fail"}
            label="Escrow funded"
            detail={escrowDetail}
            testId="closing-step-1-preflight-escrow"
          />
        </ul>
      </Card>

      <Card>
        <header className="mb-space-3">
          <h4 className="font-display text-h4 text-onyx mt-0 mb-space-1">
            Closing Certifications
          </h4>
          <p className="text-caption text-gray-mid m-0">
            Certify, decline, or escalate each closing certification. Open the
            form to record your severity assessment, report attestation, and
            justification. Declining a certification flags an upstream issue
            for remediation; it does not roll back the deal stage.
          </p>
        </header>
        <ul
          className="flex flex-col gap-space-2 m-0 p-0 list-none"
          data-testid="closing-certifications"
        >
          {CERTS.map((cert) => (
            <CertificationItemRow
              key={cert.certificationCode}
              dealId={dealId}
              tab="Closing"
              section={1}
              cert={cert}
              row={rowByCode.get(cert.certificationCode) ?? null}
              readOnly={readOnly}
            />
          ))}
        </ul>
      </Card>
    </div>
  );
}

/** The escrow pre-flight sentence, per escrow model. */
function describeEscrow(
  status: DealEscrowStatusDto | undefined,
  legacyAmount: number | null,
  date: (iso: string) => string,
): string {
  if (!status) return "Loading escrow status…";
  switch (status.model) {
    case "Legacy":
      return status.fundingVerified && status.fundingVerifiedAt
        ? `${formatCurrency(legacyAmount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} verified by A1 on ${date(status.fundingVerifiedAt)}`
        : "Awaiting A1's funding verification (Underwriting Section 2)";
    case "EscrowCom":
      if (!status.fundingVerified)
        return "Awaiting Buyer Funded on the deal's Escrow.com transaction (Underwriting Section 2)";
      return status.released && status.releasedAt
        ? `The buyer funded the deal's Escrow.com transaction; released on ${date(status.releasedAt)}`
        : "The buyer funded the deal's Escrow.com transaction";
    default:
      return "No escrow is set up yet (Underwriting Section 2)";
  }
}

function CheckRow({
  status,
  label,
  detail,
  testId,
}: {
  status: CheckStatus;
  label: string;
  detail: string;
  testId?: string;
}) {
  return (
    <li
      className="flex items-start gap-space-3 px-space-3 py-space-2 border border-border-light rounded-md bg-white-pure"
      data-status={status}
      data-testid={testId}
    >
      <span
        className={
          status === "ok"
            ? "text-status-success"
            : status === "na"
              ? "text-gray-mid"
              : "text-status-error"
        }
        aria-label={CHECK_LABEL[status]}
      >
        {status === "ok" ? (
          <Check size={18} aria-hidden />
        ) : status === "na" ? (
          <Minus size={18} aria-hidden />
        ) : (
          <X size={18} aria-hidden />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <div className="font-display text-small text-onyx m-0">{label}</div>
        <p className="mt-space-1 text-caption text-gray-mid m-0">{detail}</p>
      </div>
    </li>
  );
}
